'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { ArrowPathIcon, DocumentTextIcon, HomeIcon, UserIcon, UserGroupIcon, Cog6ToothIcon, ArrowLeftOnRectangleIcon, FolderIcon } from '@heroicons/react/24/outline';
import { DocumentTextIcon as DocumentTextSolidIcon, HomeIcon as HomeSolidIcon, UserIcon as UserSolidIcon, UserGroupIcon as UserGroupSolidIcon, Cog6ToothIcon as Cog6ToothSolidIcon, FolderIcon as FolderSolidIcon } from '@heroicons/react/24/solid';
import { useRefresh } from '@/context/RefreshContext';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

/**
 * Modern mobile-first bottom navigation component with glassmorphism effect
 */
const BottomNavigation: React.FC = () => {
  const pathname = usePathname();
  const router = useRouter();

  // Hide nav on public/auth pages
  if (pathname === '/welcome' || pathname === '/login') return null;
  const { performHardRefresh, isRefreshing } = useRefresh();
  const { logOut } = useAuth();
  
  const handleLogout = async () => {
    try {
      await logOut();
      router.push('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Navigation item type
  type NavItem = {
    path: string;
    label: string;
    icon: React.ReactNode;
    activeIcon: React.ReactNode;
    onClick?: () => void;
    badge?: number;
  };

  const navItems: NavItem[] = [
    { 
      path: '/', 
      label: 'Chat',
      icon: <HomeIcon className="h-5 w-5" />,
      activeIcon: <HomeSolidIcon className="h-5 w-5" />
    },
    { 
      path: '/clients', 
      label: 'Clients',
      icon: <UserGroupIcon className="h-5 w-5" />,
      activeIcon: <UserGroupSolidIcon className="h-5 w-5" />
    },
    { 
      path: '/tools', 
      label: 'Tools',
      icon: <DocumentTextIcon className="h-5 w-5" />,
      activeIcon: <DocumentTextSolidIcon className="h-5 w-5" />
    },
    { 
      path: '/content', 
      label: 'Content',
      icon: <FolderIcon className="h-5 w-5" />,
      activeIcon: <FolderSolidIcon className="h-5 w-5" />
    },
    { 
      path: '/settings', 
      label: 'Settings',
      icon: <Cog6ToothIcon className="h-5 w-5" />,
      activeIcon: <Cog6ToothIcon className="h-5 w-5" />
    }
  ];

  const isActive = (itemPath: string) => {
    if (itemPath === '/') return pathname === '/';
    return pathname.startsWith(itemPath);
  };

  // Preview pages provide their own harness layouts and should not be covered by app nav chrome.
  if (pathname?.startsWith('/preview')) {
    return null;
  }

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50" data-testid="bottom-nav">
      {/* iOS 26 Liquid Glass background */}
      <div className="liquid-glass border-t border-white/10 shadow-lg"
           style={{
             background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%), rgba(0, 0, 0, 0.1)',
             backdropFilter: 'blur(24px) saturate(180%)',
           }}>
        <div className="flex justify-around items-center h-16 px-2 pb-safe">
            {navItems.map((item) => {
              const active = isActive(item.path);
              
              return item.onClick ? (
                <button
                  key={item.path}
                  onClick={item.onClick}
                  disabled={isRefreshing}
                  aria-label={`${item.label} the application`}
                  className={cn(
                    'flex flex-col items-center justify-center min-w-[44px] min-h-[44px] px-2 py-1 rounded-xl transition-all duration-200',
                    'hover:bg-muted/50 active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/50',
                    isRefreshing && 'text-primary'
                  )}
                >
                  <div className="relative">
                    <ArrowPathIcon className={cn('h-5 w-5', isRefreshing && 'animate-spin')} />
                    {item.badge && (
                      <div className="absolute -top-1 -right-1 h-3 w-3 bg-destructive rounded-full flex items-center justify-center">
                        <span className="text-[10px] text-destructive-foreground font-medium">
                          {item.badge > 9 ? '9+' : item.badge}
                        </span>
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] mt-0.5 font-medium truncate">
                    {isRefreshing ? 'Syncing...' : item.label}
                  </span>
                </button>
              ) : (
                <Link
                  key={item.path}
                  href={item.path}
                  className={cn(
                    'flex flex-col items-center justify-center min-w-[44px] min-h-[44px] px-2 py-1 rounded-xl transition-all duration-200',
                    'hover:bg-muted/50 active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/50',
                    active 
                      ? 'text-primary bg-primary/10' 
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <div className="relative">
                    {active ? item.activeIcon : item.icon}
                    {item.badge && (
                      <div className="absolute -top-1 -right-1 h-3 w-3 bg-destructive rounded-full flex items-center justify-center">
                        <span className="text-[10px] text-destructive-foreground font-medium">
                          {item.badge > 9 ? '9+' : item.badge}
                        </span>
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] mt-0.5 font-medium truncate">
                    {item.label}
                  </span>
                </Link>
              );
            })}
        </div>
      </div>
    </nav>
  );
};

export default BottomNavigation;
